/**
 * Export utilities for reports
 */

import ExcelJS from 'exceljs';

/**
 * Convert data array to CSV string
 * @param {Array} data - Array of objects to convert
 * @param {Array} columns - Column definitions with header and accessor
 * @returns {string} CSV string
 */
export function convertToCSV(data, columns) {
  if (!data || data.length === 0) return '';

  // Create header row
  const headers = columns.map(col => `"${col.header}"`).join(',');

  // Create data rows
  const rows = data.map((row, index) => {
    return columns.map(col => {
      let value;
      if (col.csvValue) {
        // Custom CSV value function
        value = col.csvValue(row[col.accessor], row, index);
      } else if (col.accessor === 'serial') {
        value = index + 1;
      } else {
        value = row[col.accessor];
      }

      // Handle null/undefined
      if (value === null || value === undefined) {
        value = '';
      }

      // Escape quotes and wrap in quotes
      const stringValue = String(value).replace(/"/g, '""');
      return `"${stringValue}"`;
    }).join(',');
  });

  return [headers, ...rows].join('\n');
}

/**
 * Download data as CSV file
 * @param {Array} data - Array of objects to export
 * @param {Array} columns - Column definitions with header and accessor
 * @param {string} filename - Name of the file (without extension)
 */
export function downloadCSV(data, columns, filename) {
  const csv = convertToCSV(data, columns);

  if (!csv) {
    console.warn('No data to export');
    return;
  }

  // Add BOM for Excel UTF-8 compatibility
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });

  // Create download link
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}_${formatDateForFilename()}.csv`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up
  URL.revokeObjectURL(url);
}

/**
 * Download data as Excel file
 * @param {Array} data - Array of objects to export
 * @param {Array} columns - Column definitions with header and accessor
 * @param {string} filename - Name of the file (without extension)
 */
export async function downloadExcel(data, columns, filename) {
  if (!data || data.length === 0) {
    console.warn('No data to export');
    return;
  }

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Report');

  // Add headers
  const headers = columns.map(col => col.header);
  worksheet.addRow(headers);

  // Style headers
  const headerRow = worksheet.getRow(1);
  headerRow.font = { bold: true };
  headerRow.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE6E6FA' }
  };

  // Add data rows
  data.forEach((row, index) => {
    const rowData = columns.map(col => {
      let value;
      if (col.csvValue) {
        // Use same logic as CSV for consistency
        value = col.csvValue(row[col.accessor], row, index);
      } else if (col.accessor === 'serial') {
        value = index + 1;
      } else {
        value = row[col.accessor];
      }

      // Handle null/undefined
      if (value === null || value === undefined) {
        value = '';
      }

      return value;
    });
    worksheet.addRow(rowData);
  });

  // Auto-fit columns
  worksheet.columns.forEach(column => {
    let maxLength = 0;
    column.eachCell(cell => {
      const cellValue = cell.value ? cell.value.toString() : '';
      maxLength = Math.max(maxLength, cellValue.length);
    });
    column.width = Math.min(maxLength + 2, 50); // Max width 50
  });

  // Generate buffer
  const buffer = await workbook.xlsx.writeBuffer();

  // Create download link
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}_${formatDateForFilename()}.xlsx`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up
  URL.revokeObjectURL(url);
}

/**
 * Format current date for filename
 * @returns {string} Date string in YYYY-MM-DD format
 */
function formatDateForFilename() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
